import React, { useState } from 'react';
import { useUserMigration } from '../hooks/useUserMigration';
import {
  Card,
  Upload,
  Button,
  Alert,
  Typography,
  Space,
  Divider,
  Modal,
  Table,
  Tag,
  Progress,
  Descriptions,
  message,
  Collapse,
  Steps,
  Result
} from 'antd';
import {
  UploadOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  DatabaseOutlined,
  FileTextOutlined
} from '@ant-design/icons';
import type { UploadProps, UploadFile } from 'antd';

const { Title, Text, Paragraph } = Typography;
const { Panel } = Collapse;
const { Step } = Steps;

interface MigrationResult {
  successfulMigrations: string[];
  errors: Record<string, string>;
  migrationTime: string;
  successCount: number;
  errorCount: number;
  totalProcessed: number;
}

interface UserMigrationPanelProps {
  // No props needed - component will use the useUserMigration hook directly
}

export const UserMigrationPanel: React.FC<UserMigrationPanelProps> = () => {
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [showExportQuery, setShowExportQuery] = useState(false);
  const [showCsvFormat, setShowCsvFormat] = useState(false);

  // Use the React Query-based migration hook
  const {
    migrationEnabled,
    migrationStatus,
    exportQuery,
    csvFormat,
    statusLoading,
    exportQueryLoading,
    csvFormatLoading,
    importLoading,
    importError,
    importUsersFromCsv,
    importUsersFromCsvAsync,
    downloadExportQuery,
    downloadCsvTemplate,
    validateCsvFile,
    importResult,
    isImportSuccess,
    resetImport,
  } = useUserMigration();
  
  const [currentStep, setCurrentStep] = useState(0);

  if (!migrationEnabled) {
    return (
      <Card>
        <Result
          icon={<DatabaseOutlined />}
          title="User Migration Not Enabled"
          subTitle="User migration from legacy system is currently disabled. Contact your system administrator to enable this feature."
          extra={
            <Alert
              message="Configuration Required"
              description="Set scalefi.migration.users.enabled=true in application properties to enable user migration functionality."
              type="info"
              showIcon
            />
          }
        />
      </Card>
    );
  }

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.error('Please select a CSV file to upload');
      return;
    }

    // Extract the file from the upload file object
    const uploadFile = fileList[0];
    const file = uploadFile.originFileObj;
    
    if (!file) {
      message.error('No file provided');
      return;
    }
    
    // Validate file before upload
    const validation = validateCsvFile(file);
    if (!validation.valid) {
      message.error(validation.message);
      return;
    }

    setCurrentStep(2);

    try {
      await importUsersFromCsvAsync(file as File);
      setShowResults(true);
      setCurrentStep(3);
    } catch (error) {
      message.error('Migration failed: ' + (error as Error).message);
      setCurrentStep(1);
    }
  };

  const handleGetExportQuery = () => {
    setShowExportQuery(true);
  };

  const handleGetCsvFormat = () => {
    setShowCsvFormat(true);
  };

  const handleDownloadExportQuery = async () => {
    await downloadExportQuery();
  };

  const handleDownloadCsvTemplate = async () => {
    await downloadCsvTemplate();
  };

  // Upload configuration
  const uploadProps: UploadProps = {
    beforeUpload: (file) => {
      const validation = validateCsvFile(file);
      if (!validation.valid) {
        message.error(validation.message);
        return false;
      }
      // Create proper UploadFile structure
      const uploadFile: UploadFile = {
        uid: file.name + Date.now(),
        name: file.name,
        status: 'done',
        originFileObj: file,
      };
      setFileList([uploadFile]);
      return false; // Prevent auto upload
    },
    fileList,
    onRemove: () => {
      setFileList([]);
    },
    accept: '.csv',
    maxCount: 1,
  };

  if (statusLoading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Progress type="circle" percent={50} />
          <p style={{ marginTop: '16px' }}>Checking migration status...</p>
        </div>
      </Card>
    );
  }

  return (
    <div>
      <Card title="User Migration from Legacy System" style={{ marginBottom: 24 }}>
        <Alert
          message="Migration Process"
          description="This tool helps you migrate users from the legacy SACCO Portal system to the new ScaleFi system. Existing users will be skipped automatically."
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
        />

        <Steps current={currentStep} style={{ marginBottom: 24 }}>
          <Step title="Export Data" description="Export users from legacy system" />
          <Step title="Upload CSV" description="Upload the exported CSV file" />
          <Step title="Import Users" description="Process and import users" />
          <Step title="Review Results" description="Review migration results" />
        </Steps>

        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Step 1: Export Instructions */}
          <Card size="small" title="Step 1: Export Legacy Data">
            <Space>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleDownloadExportQuery}
                loading={exportQueryLoading}
              >
                Download Export Query
              </Button>
              <Button
                icon={<FileTextOutlined />}
                onClick={handleGetExportQuery}
              >
                View Export Query
              </Button>
              <Button
                icon={<InfoCircleOutlined />}
                onClick={handleGetCsvFormat}
              >
                CSV Format Info
              </Button>
            </Space>
          </Card>

          {/* Step 2: Upload CSV */}
          <Card size="small" title="Step 2: Upload CSV File">
            <Upload.Dragger {...uploadProps}>
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">Click or drag CSV file to this area to upload</p>
              <p className="ant-upload-hint">
                Support for single CSV file upload. File must be less than 10MB.
              </p>
            </Upload.Dragger>
            
            <div style={{ marginTop: 16 }}>
              <Button
                type="primary"
                onClick={handleUpload}
                disabled={fileList.length === 0}
                loading={importLoading}
                size="large"
              >
                Import Users
              </Button>
              <Button
                style={{ marginLeft: 8 }}
                onClick={handleDownloadCsvTemplate}
              >
                Download Template
              </Button>
            </div>
          </Card>

          {/* Step 3: Results */}
          {isImportSuccess && importResult && (
            <Card size="small" title="Step 3: Migration Results">
              <Result
                status="success"
                title="Migration Completed Successfully!"
                subTitle={`${importResult.successCount} users imported, ${importResult.errorCount} users skipped or failed`}
                extra={[
                  <Button key="details" onClick={() => setShowResults(true)}>
                    View Details
                  </Button>,
                  <Button key="reset" onClick={() => {
                    resetImport();
                    setFileList([]);
                    setCurrentStep(0);
                  }}>
                    Start New Migration
                  </Button>
                ]}
              />
            </Card>
          )}

          {importError && (
            <Card size="small" title="Migration Error">
              <Result
                status="error"
                title="Migration Failed"
                subTitle={importError.message}
                extra={[
                  <Button key="retry" type="primary" onClick={handleUpload}>
                    Retry Migration
                  </Button>
                ]}
              />
            </Card>
          )}
        </Space>
      </Card>

      {/* Export Query Modal */}
      <Modal
        title="Legacy User Export Query"
        open={showExportQuery}
        onCancel={() => setShowExportQuery(false)}
        footer={[
          <Button key="download" type="primary" onClick={handleDownloadExportQuery}>
            Download SQL File
          </Button>,
          <Button key="close" onClick={() => setShowExportQuery(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {exportQuery && (
          <div>
            <Paragraph>
              <Text strong>Instructions:</Text> {exportQuery.instructions}
            </Paragraph>
            <Divider />
            <Text code style={{ whiteSpace: 'pre-wrap', display: 'block', background: '#f5f5f5', padding: 16 }}>
              {exportQuery.query}
            </Text>
          </div>
        )}
      </Modal>

      {/* CSV Format Modal */}
      <Modal
        title="CSV Format Documentation"
        open={showCsvFormat}
        onCancel={() => setShowCsvFormat(false)}
        footer={[
          <Button key="template" type="primary" onClick={handleDownloadCsvTemplate}>
            Download Template
          </Button>,
          <Button key="close" onClick={() => setShowCsvFormat(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {csvFormat && (
          <div>
            <Title level={5}>Required CSV Headers:</Title>
            <Text code>{csvFormat.headers?.join(', ')}</Text>
            
            <Divider />
            
            <Title level={5}>Example Row:</Title>
            <Text code style={{ display: 'block', background: '#f5f5f5', padding: 8 }}>
              {csvFormat.example}
            </Text>
            
            {csvFormat.notes && (
              <>
                <Divider />
                <Title level={5}>Important Notes:</Title>
                <ul>
                  {csvFormat.notes.map((note: string, index: number) => (
                    <li key={index}>{note}</li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </Modal>

      {/* Results Modal */}
      <Modal
        title="Migration Results Details"
        open={showResults}
        onCancel={() => setShowResults(false)}
        footer={[
          <Button key="close" onClick={() => setShowResults(false)}>
            Close
          </Button>
        ]}
        width={800}
      >
        {importResult && (
          <div>
            <Descriptions bordered size="small" style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Total Processed">{importResult.totalProcessed}</Descriptions.Item>
              <Descriptions.Item label="Successfully Imported">{importResult.successCount}</Descriptions.Item>
              <Descriptions.Item label="Skipped/Failed">{importResult.errorCount}</Descriptions.Item>
            </Descriptions>

            {importResult.successfulMigrations && importResult.successfulMigrations.length > 0 && (
              <>
                <Title level={5}>Successfully Imported Users:</Title>
                <div style={{ maxHeight: 200, overflow: 'auto', marginBottom: 16 }}>
                  {importResult.successfulMigrations.map((userId: string) => (
                    <Tag key={userId} color="green" style={{ margin: 2 }}>
                      {userId}
                    </Tag>
                  ))}
                </div>
              </>
            )}

            {importResult.errors && Object.keys(importResult.errors).length > 0 && (
              <>
                <Title level={5}>Errors/Skipped Users:</Title>
                <Table
                  size="small"
                  dataSource={Object.entries(importResult.errors).map(([userId, error]) => ({
                    key: userId,
                    userId,
                    error
                  }))}
                  columns={[
                    { title: 'User ID', dataIndex: 'userId', key: 'userId' },
                    { title: 'Reason', dataIndex: 'error', key: 'error' }
                  ]}
                  pagination={{ pageSize: 10 }}
                />
              </>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};
